import axiosInstance from './axiosInstance';

export interface MakeAndModel {
  _id: string;
  name: string;
  description?: string;
  manufacturerId: string;
  specifications?: {
    capacity?: number;
    voltage?: number;
    power?: number;
    efficiency?: number;
    dimensions?: {
      length?: number;
      width?: number;
      height?: number;
      weight?: number;
      unit: 'mm' | 'cm' | 'm' | 'kg';
    };
    [key: string]: any;
  };
  status: 'active' | 'inactive';
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateMakeAndModelData {
  name: string;
  description?: string;
  manufacturerId: string;
  specifications?: {
    capacity?: number;
    voltage?: number;
    power?: number;
    efficiency?: number;
    dimensions?: {
      length?: number;
      width?: number;
      height?: number;
      weight?: number;
      unit: 'mm' | 'cm' | 'm' | 'kg';
    };
    [key: string]: any;
  };
  status?: 'active' | 'inactive';
}

export interface UpdateMakeAndModelData {
  name?: string;
  description?: string;
  manufacturerId?: string;
  specifications?: {
    capacity?: number;
    voltage?: number;
    power?: number;
    efficiency?: number;
    dimensions?: {
      length?: number;
      width?: number;
      height?: number;
      weight?: number;
      unit: 'mm' | 'cm' | 'm' | 'kg';
    };
    [key: string]: any;
  };
  status?: 'active' | 'inactive';
}

export interface MakeAndModelListParams {
  page?: number;
  limit?: number;
  search?: string;
  manufacturerId?: string;
  status?: 'active' | 'inactive';
  sortBy?: string;
}

class MakeAndModelService {
  /**
   * Get all make and models with pagination
   */
  async getMakeAndModels(params: MakeAndModelListParams = {}): Promise<{
    data: MakeAndModel[];
    meta: {
      totalDocs: number;
      limit: number;
      totalPages: number;
      page: number;
      pagingCounter: number;
      hasPrevPage: boolean;
      hasNextPage: boolean;
      prevPage: number | null;
      nextPage: number | null;
    };
  }> {
    const response = await axiosInstance.get('/make-and-models', { params });
    return response.data;
  }

  /**
   * Get make and model by ID
   */
  async getMakeAndModelById(id: string): Promise<MakeAndModel> {
    const response = await axiosInstance.get(`/make-and-models/${id}`);
    return response.data.data;
  }

  /**
   * Get make and models by manufacturer ID
   */
  async getMakeAndModelsByManufacturer(manufacturerId: string): Promise<MakeAndModel[]> {
    const response = await axiosInstance.get(`/make-and-models/by-manufacturer/${manufacturerId}`);
    return response.data.data;
  }

  /**
   * Create a new make and model
   */
  async createMakeAndModel(data: CreateMakeAndModelData): Promise<MakeAndModel> {
    const response = await axiosInstance.post('/make-and-models', data);
    return response.data.data;
  }

  /**
   * Update make and model by ID
   */
  async updateMakeAndModel(id: string, data: UpdateMakeAndModelData): Promise<MakeAndModel> {
    const response = await axiosInstance.put(`/make-and-models/${id}`, data);
    return response.data.data;
  }

  /**
   * Delete make and model by ID (soft delete)
   */
  async deleteMakeAndModel(id: string): Promise<void> {
    await axiosInstance.delete(`/make-and-models/${id}`);
  }

  /**
   * Get make and models for dropdown (active only)
   */
  async getMakeAndModelsForDropdown(manufacturerId?: string): Promise<Array<{ 
    id: string; 
    name: string; 
    manufacturerName: string 
  }>> {
    const params = manufacturerId ? { manufacturerId } : {};
    const response = await axiosInstance.get('/make-and-models/dropdown', { params });
    return response.data.data;
  }
}

export const makeAndModelService = new MakeAndModelService();
export default makeAndModelService;
